import Image from "@/components/Image";
import Masonry from "@/components/Masonry";
import { getImageSrc2x } from "@/utils/imageUrl";
import { useMemoizedFn } from "ahooks";

import classNames from "classnames";
import Link from "next/link";
import { memo, useMemo } from "react";
import styles from "./cardA.module.less";

interface IProps {
  id?: string;
  title: string;
  subTitle: string;
  pins: IPin[];
  href: string;
  height?: number;
  loading?: boolean;
  /**相似素材的样式（粉色颜色主题） */
  isSimilar?: boolean;
  width?: number;
}

function SimilarCard({
  id = "SimilarCard",
  title,
  subTitle,
  pins,
  href,
  height,
  loading,
  isSimilar,
  width = 240,
  ...rest
}: IProps & React.HTMLAttributes<HTMLAnchorElement>) {
  const renderListData: IPin[] = useMemo(() => {
    let data = [...(pins || [])];

    return data.reverse().splice(0, 15);
  }, [pins]);

  // 分离横图和其他图片
  const { horizontalImages, otherImages } = useMemo(() => {
    const horizontalList = renderListData?.filter(
      (item) => item.file.width / item.file.height > 1.5
    );
    const otherList = renderListData?.filter(
      (item) => item.file.width / item.file.height <= 1.5
    );

    return {
      horizontalImages: horizontalList,
      otherImages: otherList,
    };
  }, [renderListData]);

  const { columnSize, columnNum } = useMemo(() => {
    // 筛选出横图数量
    const horizontalList = renderListData.filter(
      (item) => item.file.width / item.file.height > 1.5
    );
    if (
      // 横图超过一半，值展示一列
      horizontalList.length >
      renderListData.length / 2
    ) {
      return {
        columnNum: 1,
        columnSize: width,
      };
    } else {
      return {
        columnNum: 2,
        columnSize: width / 2 - 1,
      };
    }
  }, [renderListData, width]);

  const render = useMemoizedFn((pin) => {
    return (
      <div key={pin.pin_id} className={styles.imgContent}>
        <Image
          onLoad={(evt) => {
            const target = evt.target as HTMLElement;
            target?.classList.add("transparent-img-bg");
          }}
          {...getImageSrc2x(pin.file, { width: columnSize })}
          width={columnSize}
          height={Math.round((pin.file?.height / pin.file?.width) * columnSize)}
          alt={pin.raw_text}
          showError={true}
        />
      </div>
    );
  });

  // 横图渲染函数
  const renderHorizontalImage = useMemoizedFn((pin) => {
    return (
      <div key={pin.pin_id} className={styles.horizontalImageContainer}>
        <Image
          onLoad={(evt) => {
            const target = evt.target as HTMLElement;
            target?.classList.add("transparent-img-bg");
          }}
          {...getImageSrc2x(pin.file, { width: width - 16 })} // 减去左右padding
          width={width - 16}
          height={Math.round(
            (pin.file?.height / pin.file?.width) * (width - 16)
          )}
          alt={pin.raw_text}
          showError={true}
        />
      </div>
    );
  });

  return (
    <Link href={href}>
      <a
        className={classNames(styles.card, { [styles.similar]: isSimilar })}
        id={id}
        {...rest}
      >
        <div className={classNames(styles.content)}>
          {/* 横图容器 */}
          {horizontalImages?.length > 0 && (
            <div className={styles.horizontalImagesContainer}>
              {horizontalImages.map(renderHorizontalImage)}
            </div>
          )}

          {/* 其他图片使用Masonry布局 */}
          {otherImages?.length > 0 && (
            <Masonry
              bricks={otherImages}
              scrollElement={id}
              brickId="pin_id"
              render={render}
              columnNum={columnNum}
              threshold={800}
              gutter={2}
              columnSize={columnSize}
              masonryKey={`${id}-${otherImages.length}-${columnSize}`}
              asyncGetContainerOffsetTop={true}
            />
          )}
        </div>
        <div className={styles.footer}>
          <div className={styles.footerContent}>
            <div className={styles.title}># {title}</div>
            <div className={styles.subtitle}> {subTitle}</div>
          </div>
        </div>
      </a>
    </Link>
  );
}
export default memo(SimilarCard);
